import { isNotDefined } from '#misc/is-not-defined.guard';

import type { EventElement, Events, ListenerOptions } from './types';

/**
 * Removes one or more event listeners from a specified element or window.
 *
 * @template E - The event type extending from the Events interface.
 * @param element - The target element or window from which to remove the event listeners.
 * @param handler - The event handler function to be removed.
 * @param options - Optional. Specifies characteristics about the event listener (e.g., capture, once, passive).
 * Defaults to `false`.
 * @param eventNames - The names of the events for which the handler should be removed.
 *
 * @remarks
 * - If the handler is not defined, the function returns early and does nothing.
 * - The function iterates over the provided event names and removes the handler for each event from the element.
 */
export function removeEventListeners<E extends Events[keyof Events]>(
  element: EventElement | Window,
  handler: (event: E) => void,
  options: ListenerOptions = false,
  ...eventNames: (keyof Pick<Events, { [K in keyof Events]: Events[K] extends E ? K : never }[keyof Events]>)[]
) {
  if (isNotDefined(handler)) {
    return;
  }

  for (const eventName of eventNames) {
    element.removeEventListener(eventName, handler as EventListener, options);
  }
}
